import { current, produce } from 'immer'

import { AiAgent, MessageType } from '../../../common/ai.dictionary'
import { AiState } from '../../ai.state'

function formatDatasourcePreview(system: any): string {
  if (!system) {
    return ''
  }

  // Database (NOSQL_DB) preview
  if (system.operations) {
    const lines: string[] = []
    lines.push(`**${system.name}** (FlowX Database)`)
    lines.push(`${system.description}`)
    lines.push(``)
    lines.push(`**Partition Key:** \`${system.partitionKey || '_id'}\``)
    lines.push(``)
    lines.push(`**Operations (${system.operations?.length || 0}):**`)
    for (const op of system.operations || []) {
      const paramCount = op.params?.length || 0
      lines.push(`- \`${op.type}\` — ${op.name} (${paramCount} params)`)
    }
    if (system.indexes?.length) {
      lines.push(``)
      lines.push(`**Indexes (${system.indexes.length}):**`)
      for (const idx of system.indexes) {
        const attrs = (idx.attributes || []).map((a: any) => `${a.name} ${a.direction}`).join(', ')
        lines.push(`- ${idx.name} (${idx.type}): ${attrs}`)
      }
    }
    return lines.join('\n')
  }

  // REST API preview
  const lines: string[] = []
  lines.push(`**${system.name}** (${system.code})`)
  lines.push(`${system.description}`)
  lines.push(``)
  lines.push(`**Base URL:** \`${system.baseUrl}\``)
  lines.push(`**Auth:** ${system.authorization?.type || 'NO_AUTH'}`)
  lines.push(``)
  lines.push(`**Endpoints (${system.endpoints?.length || 0}):**`)
  for (const ep of system.endpoints || []) {
    const method = ep.httpMethod?.toUpperCase() || '?'
    const paramCount =
      (ep.pathParameters?.length || 0) +
      (ep.queryParameters?.length || 0) +
      (ep.headers?.length || 0)
    lines.push(`- \`${method}\` \`${ep.url}\` — ${ep.name} (${paramCount} params)`)
  }
  return lines.join('\n')
}

export const onGetAnswerEventStream = (state: AiState, { response }: { response: any }): AiState =>
  produce(state, (draft: AiState) => {
    if (!current(draft).backgroundAiResponse) {
      draft.streamingAiResponse = true
      draft.pendingAiResponse = true
    }

    const { message } = response
    if (!message) {
      return
    }

    // Session handling
    if (message.answer?.operation === 'session') {
      draft.sessionId = message.answer.thread_id
      if (message.answer.request_id) {
        draft.lastRequestId = message.answer.request_id
      }
      return
    }

    // Debug/log events
    if (message.debug) {
      draft.logs.unshift({
        agent: message.agent || 'Integrator',
        message: message.debug.info,
        message_type: 'INFO',
        timestamp: new Date().toISOString(),
        status: message.debug.status,
        step: message.debug.step,
      })
      draft.currentAITaskMessage = message.debug.info
      return
    }

    // Greeting check response — suppress the raw JSON, only show if it's a chat greeting
    if (message.answer?.greetings) {
      // type "continue" means it's not a greeting — suppress entirely
      return
    }

    // Streaming preview of datasource
    if (message.answer?.operation === 'datasource_preview') {
      draft.generatedDatasource = message.answer.datasource_preview
      return
    }

    // OpenAPI parse info
    if (message.answer?.operation === 'openapi_parsed') {
      const lastMsg = draft.messages[draft.messages.length - 1]
      if (lastMsg?.type === MessageType.AiMsg && !lastMsg.commited) {
        lastMsg.message = message.answer.info
      } else {
        draft.messages.push({
          message: message.answer.info,
          type: MessageType.AiMsg,
          commited: false,
        } as any)
      }
      return
    }

    // End event — generated datasource or database
    if (message.end) {
      if (message.end.operation === 'generated_database') {
        draft.generatedDatasource = message.end.content
        draft.streamingAiResponse = false
        draft.pendingAiResponse = false

        const preview = formatDatasourcePreview(message.end.content)
        draft.messages.push({
          message: preview,
          type: MessageType.AiMsg,
          commited: true,
        } as any)
        return
      }

      if (message.end.operation === 'generated_datasource') {
        draft.generatedDatasource = message.end.content
        draft.streamingAiResponse = false
        draft.pendingAiResponse = false

        const preview = formatDatasourcePreview(message.end.content)
        draft.messages.push({
          message: preview,
          type: MessageType.AiMsg,
          commited: true,
        } as any)
        return
      }

      if (message.end.operation === 'edited_datasource') {
        draft.generatedDatasource = message.end.content
        draft.streamingAiResponse = false
        draft.pendingAiResponse = false

        const preview = formatDatasourcePreview(message.end.content)
        draft.messages.push({
          message: `**Proposed changes:**\n\n${preview}`,
          type: MessageType.AiMsg,
          commited: true,
        } as any)
        return
      }

      if (message.end.operation === 'check_greeting') {
        draft.streamingAiResponse = false
        draft.pendingAiResponse = false
        const answer = message.end.answer
        if (answer && typeof answer === 'string') {
          draft.messages.push({
            message: answer,
            type: MessageType.AiMsg,
            commited: true,
          } as any)
        }
        return
      }

      if (message.end.operation === 'error') {
        draft.streamingAiResponse = false
        draft.pendingAiResponse = false
        draft.messages.push({
          message: message.end.answer || 'An error occurred.',
          type: MessageType.AiMsg,
          commited: true,
        } as any)
        return
      }
    }

    // Fallback: ignore other answer messages to avoid raw JSON in chat
  })

export const onKeepDatasourceDraftSuccess = (
  state: AiState,
  { response }: { response: any }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message: 'Data source saved successfully.',
      type: MessageType.UserMsg,
      commited: true,
    } as any)
    draft.generatedDatasource = null

    // Update plan step if applicable
    draft.selectedPlan?.actions.forEach((action) => {
      if (action.agent === AiAgent.INTEGRATOR) {
        action.steps = action.steps.map((step) =>
          step.step_number === draft.currentPlanStep?.step_number
            ? { ...step, step_status: 'completed', executionOutput: response }
            : step
        )
      }
    })
  })

export const onDiscardDatasourceDraft = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    draft.generatedDatasource = null
    draft.messages.push({
      message: 'Data source draft discarded.',
      type: MessageType.UserMsg,
      commited: true,
    } as any)
  })

// Legacy agent handler
export const onKeepAgentDraftSuccess = (state: AiState, { response }: { response: any }): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message: 'Keep the generated agent.',
      type: MessageType.UserMsg,
      commited: true,
    } as any)
    draft.selectedPlan?.actions.forEach((action) => {
      if (action.agent === AiAgent.INTEGRATOR) {
        action.steps = action.steps.map((step) =>
          step.step_number === draft.currentPlanStep?.step_number
            ? { ...step, step_status: 'completed', executionOutput: response }
            : step
        )
      }
    })
    draft.generatedAgentWorkflow = null
  })
